import React from 'react'
import moment from 'moment'
import colors from './helpers/colors'
import { percentChange, toPercent } from './helpers/handleNumbers'
import d3SummaryBars from './visualizations/d3SummaryBars'
import D3Wrapper from './visualizations/D3Wrapper'
import removeEmpty from './helpers/removeEmpty'

const Row = React.memo(
  ({ terms, getData, chartDataset, name, item, color, style }) => (
    <tr style={style || {}}>
      <th scope="row">{name}</th>
      {/* First three totals */}
      {terms.map(term => (
        <td key={term} style={{ textAlign: 'right' }}>
          {getData(term, item, true)}
        </td>
      ))}
      {/* last year to this year */}
      <td style={{ textAlign: 'right' }}>
        {item==='enrolled'
          ? ' '
          : (getData(terms[2], item) - getData(terms[1], item)).toLocaleString()
        }
      </td>
      {/* Percent Change */}
      <td style={{ textAlign: 'right' }}>
        {item==='enrolled'
          ? ' '
          : percentChange(getData(terms[2], item), getData(terms[1], item))+'%'
        }
      </td>
      {/* Chart */}
      <td style={{ textAlign: 'left', maxWidth: '300px', padding: '5px' }}>
        <D3Wrapper
          width="300"
          height="75"
          data={chartDataset[item]}
          render={(svg, data) => d3SummaryBars(svg, 300, 75, data, false, color)}
        />
      </td>
    </tr>
  )
)

function EnrollmentPredictors({ filters, setFilterOptions }) {
  const [data, setData] = React.useState(null)
  const [fetching, setFetching] = React.useState(false)
  const terms = React.useMemo(() => (data ? data.terms.sort() : []), [data])

  React.useEffect(() => {
    setFetching(true)
    fetch(`${window.__api}/latest/summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: JSON.stringify({
        filters: removeEmpty(filters),
      }),
    })
      .then(res => res.json())
      .then(res => {
        setFetching(false)
        setData(res)
        setFilterOptions(res.filters)
      })
      .catch(err => {
        console.error(err)
      })
  }, [filters, setFilterOptions])

  const getData = React.useCallback(
    (term, key, toString = false) => {
      if (key==='enrolled') {
        const termData = data && data.enrolled[term]
        if (!termData || !termData[0]) return null
        const val = parseInt(termData[0].enrolledFall)
        return toString ? (val || 0).toLocaleString() : val || 0
      }
      let val = data ? data[term][0][key] : null
      if (val<3) val = 0
      return toString ? (val || 0).toLocaleString() : val || 0
    },
    [data]
  )

  const chartData = React.useCallback(
    item =>
      terms.map(term => ({
        name: term.slice(0, 4),
        value: item
          ? getData(term, item)
          : toPercent(getData(term, 'admitted'), getData(term, 'applied')),
      })),
    [getData, terms]
  )

  const chartDataset = React.useMemo(
    () => ({
      applied: chartData('applied'),
      admitted: chartData('admitted'),
      deposited: chartData('deposited'),
      housing: chartData('housing'),
      orientation: chartData('orientation'),
      enrolled: data && data.enrolled && chartData('enrolled'),
      pct: chartData(),
    }),
    [chartData]
  )

  const rendered = React.useMemo(
    () =>
      data ? (
        <table
          className="admissions"
          style={{ opacity: fetching ? '0.5' : '1' }}
        >
          <thead>
            <tr style={{ borderBottom: 'none' }}>
              <th scope="colgroup" />
              <th colSpan="3" scope="colgroup" style={{ textAlign: 'center' }}>
                As of{' '}
                {moment(getData(terms.slice(-1), 'asOfDate'))
                  .subtract(1, 'days')
                  .format('MMMM Do')}
                <div style={{color:'#555',fontWeight:'normal'}}>(admit year)</div>
              </th>
              <th colSpan="2" scope="colgroup" style={{ textAlign: 'center' }}>
                {terms.slice(-2)[0].slice(0, 4)}
                &nbsp;vs&nbsp;
                {terms.slice(-1)[0].slice(0, 4)}
              </th>
              <th />
            </tr>
            <tr>
              <th>Enrollment Predictors</th>
              {terms.map(term => (
                <th key={term} style={{ textAlign: 'right' }}>
                  {getData(term, 'asOfDate').slice(0, 4)} 
                  <div style={{color:'#555',fontWeight:'normal'}}>({term.slice(0,4)})</div>
                </th>
              ))}

              <th style={{ textAlign: 'right' }}># Change</th>
              <th
                style={{
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                }}
              >
                % Change
              </th>
              <th>Comparison</th>
            </tr>
          </thead>
          <tbody>
            <Row
              terms={terms}
              getData={getData}
              chartDataset={chartDataset}
              name="Applicants"
              item="applied"
            />
            {/* <Row
                terms={terms}
                getData={getData}
                chartDataset={chartDataset}
                name="Admitted"
                item="admitted"
                color={colors.admitted[0]}
              /> */}
            <tr>
              <th scope="row">
                Admitted
                <br />
                <small style={{ fontWeight: 'normal' }}>% Admitted</small>
              </th>
              {terms.map(term => (
                <td key={term} style={{ textAlign: 'right' }}>
                  {getData(term, 'admitted').toLocaleString()}
                  <br />
                  <small>
                    {toPercent(
                      getData(term, 'admitted'),
                      getData(term, 'applied')
                    )}
                    %
                  </small>
                </td>
              ))}
              <td style={{ textAlign: 'right' }}>
                {(
                  getData(terms[2], 'admitted') - getData(terms[1], 'admitted')
                ).toLocaleString()}
              </td>
              <td style={{ textAlign: 'right' }}>
                {percentChange(
                  getData(terms[2], 'admitted'),
                  getData(terms[1], 'admitted')
                )}
                %
              </td>
              <td
                style={{ textAlign: 'left', maxWidth: '300px', padding: '5px' }}
              >
                <D3Wrapper
                  width="300"
                  height="75"
                  data={chartDataset['admitted']}
                  render={(svg, data) => d3SummaryBars(svg, 300, 75, data)}
                />
              </td>
            </tr>
            <Row
              terms={terms}
              getData={getData}
              chartDataset={chartDataset}
              name="Deposits"
              item="deposited"
            />
            <Row
              terms={terms}
              getData={getData}
              chartDataset={chartDataset}
              name="Housing"
              item="housing"
            />
            <Row
              terms={terms}
              getData={getData}
              chartDataset={chartDataset}
              name="Orientation"
              item="orientation"
            />
            {data.enrolled &&
              <Row
                terms={terms}
                getData={getData}
                chartDataset={chartDataset}
                name="Census Enrollment"
                item="enrolled"
                style={{borderTop:'5px solid #999'}}
                color={colors.deposited[0]}
              />
            }
          </tbody>
        </table>
      ) : (
        <p>Loading...</p>
      ),
    [data, chartDataset, getData, terms, fetching]
  )

  return rendered
}

export default React.memo(EnrollmentPredictors)
